/**
 * Transactions History Page
 * 
 * หน้าประวัติฝาก-ถอน ทั้งหมด
 * - แสดงทุกสถานะ (Pending, Approved, Rejected)
 * - มี Filter สถานะ และ วันที่
 */

import { useState, useEffect, useCallback } from 'react'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  ArrowDownToLine,
  ArrowUpFromLine,
  TrendingUp,
  History,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  Loader2,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { useAuth } from '@/components/auth'
import { useCurrency } from '@/stores/currency-store'
import { TransactionService } from '@/domain/transaction'
import { getUserService } from '@/domain/user'
import { toast } from 'sonner'
import {
  TransactionTable,
  TransactionFiltersSection,
  TransactionDialog,
  type Transaction,
  type TransactionFilters,
  type TransactionDialogMode,
  type TransactionFormData,
} from './components'
import { MemberDetailDialog } from '@/features/members/member-detail-dialog'
import type { Member } from '@/domain/user/user.types'

// ============================================
// Main Component
// ============================================

export function TransactionsHistoryPage() {
  const { sessionToken } = useAuth()
  const { format: formatCurrency } = useCurrency()
  
  // State
  const [transactions, setTransactions] = useState<Transaction[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [page, setPage] = useState(1)
  const [limit, setLimit] = useState(20)
  const [totalCount, setTotalCount] = useState(0)
  
  // Dialog State
  const [dialogOpen, setDialogOpen] = useState(false)
  const [dialogMode, setDialogMode] = useState<TransactionDialogMode>('view')
  const [selectedTransaction, setSelectedTransaction] = useState<Transaction | null>(null)
  
  // Member Detail Dialog State
  const [memberDialogOpen, setMemberDialogOpen] = useState(false)
  const [selectedMember, setSelectedMember] = useState<Member | null>(null)
  const [isLoadingMember, setIsLoadingMember] = useState(false)
  
  // Filters
  const [filters, setFilters] = useState<TransactionFilters>({
    searchText: '',
    type: '',
    channel: '',
    bankType: '',
    status: '',
    dateFrom: '',
    dateTo: '',
  })
  
  const totalPages = Math.ceil(totalCount / limit)
  
  // Stats (today)
  const todayDeposits = transactions.filter(t => t.type === 'Deposit' && t.status === 'Approved')
  const todayWithdraws = transactions.filter(t => t.type === 'Withdraw' && t.status === 'Approved')
  const totalDepositToday = todayDeposits.reduce((sum, t) => sum + t.amount, 0)
  const totalWithdrawToday = todayWithdraws.reduce((sum, t) => sum + t.amount, 0)
  const netProfit = totalDepositToday - totalWithdrawToday
  
  // Load data from API
  const loadData = useCallback(async () => {
    setIsLoading(true)
    
    try {
      const token = sessionToken || sessionStorage.getItem('admin_session_token') || localStorage.getItem('admin_session_token')
      
      if (!token) {
        console.error('No session token')
        setIsLoading(false)
        return
      }
      
      // 🔗 Load all transactions from API
      console.log('📡 [HistoryPage] Loading transactions...', { filters, page, limit })
      
      const result = await TransactionService.findAll(token, {
        type: filters.type as 'Deposit' | 'Withdraw' | undefined,
        status: filters.status as 'Pending' | 'Approved' | 'Rejected' | undefined,
        startDate: filters.dateFrom || undefined,
        endDate: filters.dateTo || undefined,
        page,
        limit,
      })
      
      console.log('✅ [HistoryPage] Loaded:', result.total, 'transactions')
      
      // Map API response to Transaction type
      let items: Transaction[] = result.items.map(tx => ({
        id: tx.id,
        type: tx.type,
        createFrom: (tx.createFrom || 'Member') as 'Panel' | 'Member' | 'Transfer',
        amount: tx.amount,
        status: tx.status,
        memberId: tx.memberId,
        memberUsername: tx.memberUsername,
        memberName: tx.memberName,
        memberBankName: tx.memberBankName,
        memberBankAccount: tx.memberBankAccount,
        memberAgentStatus: tx.memberAgentStatus,
        isBonus: tx.isBonus,
        webBankName: tx.webBankName,
        webBankAccount: tx.webBankAccount,
        webBankAccountName: tx.webBankAccountName,
        webBankId: tx.webBankId,
        withdrawBy: tx.withdrawBy as 'Auto' | 'Manual' | 'Manaul' | undefined,
        isCreditWithdraw: tx.isCreditWithdraw,
        isCashWithdraw: tx.isCashWithdraw,
        isCreditWithdrawAmount: tx.isCreditWithdrawAmount,
        isCashWithdrawAmount: tx.isCashWithdrawAmount,
        uuid: tx.uuid,
        apiBankObj: tx.apiBankObj,
        notes: tx.notes,
        statusNote: tx.statusNote,
        promotionCode: tx.promotionCode,
        dateTime: tx.dateTime,
        datetimeCreated: tx.dateTime,
        transferToBankName: tx.transferToBankName,
        transferToBankAccount: tx.transferToBankAccount,
        transferToAccountName: tx.transferToAccountName,
        transferFromBankName: tx.transferFromBankName,
        transferFromBankAccount: tx.transferFromBankAccount,
        transferFromAccountName: tx.transferFromAccountName,
        createdAt: tx.createdAt,
        updatedAt: tx.updatedAt,
        updatedBy: tx.updatedBy,
        createdBy: tx.createdBy,
        channel: tx.channel || "",
      }))
      
      // Apply client-side filters (search text, channel, bankType)
      if (filters.searchText) {
        const search = filters.searchText.toLowerCase()
        items = items.filter(tx =>
          tx.memberUsername?.toLowerCase().includes(search) ||
          tx.memberName?.toLowerCase().includes(search) ||
          tx.memberBankAccount?.includes(search)
        )
      }
      if (filters.channel) {
        items = items.filter(tx => tx.createFrom === filters.channel)
      }
      if (filters.bankType === 'bonus') {
        items = items.filter(tx => tx.isBonus)
      } else if (filters.bankType === 'bank') {
        items = items.filter(tx => !tx.isBonus)
      }
      
      setTransactions(items)
      setTotalCount(result.total)
      
    } catch (error) {
      console.error('❌ [HistoryPage] Error loading:', error)
      toast.error('ไม่สามารถโหลดข้อมูลได้')
      setTransactions([])
      setTotalCount(0)
    } finally {
      setIsLoading(false)
    }
  }, [sessionToken, filters, page, limit])
  
  useEffect(() => {
    loadData()
  }, [loadData])
  
  // Handlers
  const handleRefresh = () => loadData()
  
  const handleViewDetail = (tx: Transaction) => {
    // เปิดเป็น Edit mode ถ้า status !== 'Approved', ไม่งั้นเป็น View mode
    const canEdit = tx.status !== 'Approved'
    setDialogMode(canEdit ? 'edit' : 'view')
    setSelectedTransaction(tx)
    setDialogOpen(true)
  }
  
  const handleSaveTransaction = async (data: TransactionFormData) => {
    console.log('Save Transaction:', data)
    toast.success('บันทึกข้อมูลสำเร็จ')
    await loadData()
  }
  
  // Action handlers (เหมือน Pending Page)
  const handleApproveManual = async (id: string) => {
    try {
      const token = sessionToken || sessionStorage.getItem('admin_session_token') || localStorage.getItem('admin_session_token')
      if (!token) {
        toast.error('กรุณาเข้าสู่ระบบใหม่')
        return
      }
      
      await TransactionService.approveManual(token, id)
      toast.success('อนุมัติรายการสำเร็จ (Manual)')
      await loadData()
    } catch (error) {
      console.error('Error approving manual:', error)
      toast.error(`เกิดข้อผิดพลาด: ${(error as Error).message}`)
    }
  }
  
  const handleApproveAuto = async (id: string) => {
    try {
      const token = sessionToken || sessionStorage.getItem('admin_session_token') || localStorage.getItem('admin_session_token')
      if (!token) {
        toast.error('กรุณาเข้าสู่ระบบใหม่')
        return
      }
      
      await TransactionService.approveAuto(token, id)
      toast.success('อนุมัติรายการสำเร็จ (Auto)')
      await loadData()
    } catch (error) {
      console.error('Error approving auto:', error)
      toast.error(`เกิดข้อผิดพลาด: ${(error as Error).message}`)
    }
  }
  
  const handleResend = async (id: string) => {
    try {
      const token = sessionToken || sessionStorage.getItem('admin_session_token') || localStorage.getItem('admin_session_token')
      if (!token) {
        toast.error('กรุณาเข้าสู่ระบบใหม่')
        return
      }
      
      await TransactionService.resend(token, id)
      toast.success('ส่งซ้ำสำเร็จ')
      await loadData()
    } catch (error) {
      console.error('Error resending:', error)
      toast.error(`เกิดข้อผิดพลาด: ${(error as Error).message}`)
    }
  }
  
  const handleReject = async (id: string) => {
    try {
      const token = sessionToken || sessionStorage.getItem('admin_session_token') || localStorage.getItem('admin_session_token')
      if (!token) {
        toast.error('กรุณาเข้าสู่ระบบใหม่')
        return
      }
      
      await TransactionService.reject(token, id)
      toast.success('ปฏิเสธรายการสำเร็จ')
      await loadData()
    } catch (error) {
      console.error('Error rejecting:', error)
      toast.error(`เกิดข้อผิดพลาด: ${(error as Error).message}`)
    }
  }
  
  const handleComplete = async (id: string) => {
    try {
      const token = sessionToken || sessionStorage.getItem('admin_session_token') || localStorage.getItem('admin_session_token')
      if (!token) {
        toast.error('กรุณาเข้าสู่ระบบใหม่')
        return
      }
      
      await TransactionService.complete(token, id)
      toast.success('ทำรายการเสร็จสิ้น')
      await loadData()
    } catch (error) {
      console.error('Error completing:', error)
      toast.error(`เกิดข้อผิดพลาด: ${(error as Error).message}`)
    }
  }
  
  // 👤 View Member Detail
  const handleViewMember = async (memberId: string) => {
    if (!memberId) {
      toast.error('ไม่พบข้อมูลสมาชิก')
      return
    }
    
    setIsLoadingMember(true)
    
    try {
      const token = sessionToken || sessionStorage.getItem('admin_session_token') || localStorage.getItem('admin_session_token')
      if (!token) {
        toast.error('กรุณาเข้าสู่ระบบใหม่')
        return
      }
      
      const userService = await getUserService()
      const member = await userService.getMember(token, memberId)
      
      if (member) {
        setSelectedMember(member)
        setMemberDialogOpen(true)
      } else {
        toast.error('ไม่พบข้อมูลสมาชิก')
      }
    } catch (error) {
      console.error('Error loading member:', error)
      toast.error('เกิดข้อผิดพลาดในการโหลดข้อมูลสมาชิก')
    } finally {
      setIsLoadingMember(false)
    }
  }
  
  // ============================================
  // Render
  // ============================================
  
  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-primary/10">
            <History className="w-6 h-6 text-primary" />
          </div>
          <div>
            <h1 className="text-xl font-bold flex items-center gap-2">
              ประวัติฝาก-ถอน
              <Badge variant="secondary">
                {totalCount} รายการ
              </Badge>
            </h1>
            <p className="text-sm text-muted-foreground">รายการฝาก-ถอนทั้งหมด</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={handleRefresh} disabled={isLoading}>
            <RefreshCw className={cn("w-4 h-4 mr-1.5", isLoading && "animate-spin")} />
            รีเฟรช
          </Button>
        </div>
      </div>
      
      {/* Stats */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card className="border-l-4 border-l-emerald-500">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">ยอดฝากวันนี้</p>
                <p className="text-2xl font-bold text-emerald-600">
                  {formatCurrency(totalDepositToday)}
                </p>
                <p className="text-xs text-muted-foreground">{todayDeposits.length} รายการ</p>
              </div>
              <div className="p-3 rounded-full bg-emerald-500/10">
                <ArrowDownToLine className="w-6 h-6 text-emerald-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-l-4 border-l-orange-500">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">ยอดถอนวันนี้</p>
                <p className="text-2xl font-bold text-orange-600">
                  {formatCurrency(totalWithdrawToday)}
                </p>
                <p className="text-xs text-muted-foreground">{todayWithdraws.length} รายการ</p>
              </div>
              <div className="p-3 rounded-full bg-orange-500/10">
                <ArrowUpFromLine className="w-6 h-6 text-orange-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-l-4 border-l-primary">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">กำไรสุทธิวันนี้</p>
                <p className={cn(
                  "text-2xl font-bold",
                  netProfit >= 0 ? "text-emerald-600" : "text-red-600"
                )}>
                  {netProfit >= 0 ? '+' : ''}{formatCurrency(netProfit)}
                </p>
                <p className="text-xs text-muted-foreground">ฝาก - ถอน</p>
              </div>
              <div className="p-3 rounded-full bg-primary/10">
                <TrendingUp className="w-6 h-6 text-primary" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
      
      {/* Filters */}
      <TransactionFiltersSection
        filters={filters}
        onChange={setFilters}
        showStatusFilter={true}
        showDateFilter={true}
      />
      
      {/* Table */}
      <Card>
        <CardContent className="p-0">
          <TransactionTable
            transactions={transactions}
            isLoading={isLoading}
            onViewDetail={handleViewDetail}
            onViewMember={handleViewMember}
            onApproveManual={handleApproveManual}
            onApproveAuto={handleApproveAuto}
            onResend={handleResend}
            onReject={handleReject}
            onComplete={handleComplete}
          />
          
          {/* Pagination */}
          {transactions.length > 0 && (
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4 p-4 border-t">
              <div className="flex items-center gap-4">
                <div className="text-sm text-muted-foreground">
                  แสดง {((page - 1) * limit) + 1} - {Math.min(page * limit, totalCount)} จาก {totalCount} รายการ
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground">แสดง</span>
                  <Select
                    value={String(limit)}
                    onValueChange={(v) => { setLimit(Number(v)); setPage(1) }}
                  >
                    <SelectTrigger className="w-[70px] h-8">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="10">10</SelectItem>
                      <SelectItem value="20">20</SelectItem>
                      <SelectItem value="50">50</SelectItem>
                      <SelectItem value="100">100</SelectItem>
                    </SelectContent>
                  </Select>
                  <span className="text-sm text-muted-foreground">รายการ</span>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(1)}
                  disabled={page === 1}
                >
                  หน้าแรก
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="text-sm px-2 min-w-[100px] text-center">
                  หน้า {page} / {totalPages || 1}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(totalPages)}
                  disabled={page >= totalPages}
                >
                  หน้าสุดท้าย
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
      
      {/* Transaction Dialog */}
      <TransactionDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        mode={dialogMode}
        transaction={selectedTransaction}
        onSave={handleSaveTransaction}
      />
      
      {/* Member Detail Dialog */}
      <MemberDetailDialog
        member={selectedMember}
        open={memberDialogOpen}
        onOpenChange={setMemberDialogOpen}
        onSaved={() => {
          loadData() // Refresh ข้อมูลหลังจากแก้ไขสมาชิก
        }}
      />
      
      {/* Loading overlay for member */}
      {isLoadingMember && (
        <div className="fixed inset-0 bg-background/50 flex items-center justify-center z-50">
          <div className="flex items-center gap-2 bg-card p-4 rounded-lg shadow-lg">
            <Loader2 className="w-5 h-5 animate-spin" />
            <span>กำลังโหลดข้อมูลสมาชิก...</span>
          </div>
        </div>
      )}
    </div>
  )
}

